
import { useEffect, useState, useRef, RefObject } from 'react';

interface ParallaxOptions {
  speed?: number;
  direction?: 'up' | 'down';
}

interface ParallaxResult<T extends HTMLElement> {
  ref: RefObject<T | null>;
  offset: number;
  style: React.CSSProperties;
}

export function useParallax<T extends HTMLElement = HTMLDivElement>(
  options: ParallaxOptions = {}
): ParallaxResult<T> {
  const { speed = 0.3, direction = 'up' } = options;
  const ref = useRef<T | null>(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      
      const rect = ref.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const elementCenter = rect.top + rect.height / 2;
      const viewportCenter = windowHeight / 2;
      const distanceFromCenter = elementCenter - viewportCenter;
      
      const parallaxOffset = distanceFromCenter * speed * (direction === 'up' ? 1 : -1);
      setOffset(parallaxOffset);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed, direction]);

  const style: React.CSSProperties = {
    transform: `translateY(${offset}px)`,
    transition: 'transform 0.1s ease-out',
  };

  return { ref, offset, style };
}

export function useBackgroundParallax(speed: number = 0.5): number {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY * speed);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return scrollY;
}
